import { useAppTranslation } from "@/utils";
import { OrderListExcelResponse, OrderStatusRequest } from "@payping/api/app/api/bnplMerchantTypes";
import { CuToman, FiFilter } from "@payping/icons";
// import { FiFilter } from "@payping/icons";
import { dateFormats, toUtc } from "@payping/utils/dateUtils";
import { Button, DatePicker, Drawer, Flex, Form, Grid, Input, Select } from "@payping/web-components";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

interface FiltersProps {
  parsedFilters: Record<string, any>;
  downloadExcel: () => Promise<OrderListExcelResponse>;
  loadingDownloadExcel: boolean;
  clearFilters: () => void;
}
const BnplListPageFilters = ({ parsedFilters, downloadExcel, loadingDownloadExcel, clearFilters }: FiltersProps) => {
  const { t } = useAppTranslation();

  const { xxl, sm, md, xl } = Grid.useBreakpoint();

  const [form] = Form.useForm();
  const [drawerForm] = Form.useForm();

  const [, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilers] = useState(false);

  const handleOpenShowFilters = () => {
    setShowFilers(true);
  };

  const handleCloseShowFilters = () => {
    setShowFilers(false);
  };

  const handleClearFilters = () => {
    clearFilters();
    setShowFilers(false);
  };

  useEffect(() => {
    form.setFieldValue("status", parsedFilters.status);
    drawerForm.setFieldValue("status", parsedFilters.status);
    drawerForm.setFieldValue(
      "creditAmount",
      !Number.isNaN(parseInt(parsedFilters.creditAmount, 10)) ? parseInt(parsedFilters.creditAmount, 10) : "",
    );
  }, [drawerForm, form, parsedFilters.creditAmount, parsedFilters.status]);

  return (
    <>
      <Form.Provider>
        <Form
          initialValues={{
            searchVariant: parsedFilters.searchVariant,
            searchValue: parsedFilters.searchValue,
          }}
          form={form}
        >
          <Flex align={xxl ? "center" : ""} className="tw-w-full" gap={"middle"} vertical={!xxl}>
            <Flex align={xl ? "center" : undefined} gap={"middle"} vertical={!xl}>
              <Flex className="tw-w-full" gap={"middle"} vertical={!md}>
                <Form.Item name="date" noStyle></Form.Item>
                <DatePicker.RangePicker
                  value={[parsedFilters.startDate, parsedFilters.endDate] as any}
                  className="tw-min-w-[288px]"
                  onChange={(dates) => {
                    if (dates === null) {
                      setSearchParams((pre) => {
                        pre.set("startDate", "");
                        pre.set("endDate", "");

                        return pre;
                      });
                    }
                    if (!dates) {
                      return;
                    }
                    setSearchParams((pre) => {
                      const startDate = dates[0]
                        ? toUtc({
                            date: dates[0],
                            toFormat: dateFormats.standard.utcDateTime,
                          }).string
                        : undefined;
                      const endDate = dates[1]
                        ? toUtc({
                            date: dates[1],
                            toFormat: dateFormats.standard.utcDateTime,
                          }).string
                        : undefined;

                      pre.set("startDate", startDate ?? "");
                      pre.set("endDate", endDate ?? "");

                      return pre;
                    });
                  }}
                />
                <Form.Item
                  noStyle
                  shouldUpdate={(prev, curr): boolean => prev.searchVariant !== curr.searchVariant}
                  key={1}
                >
                  {(form1) => (
                    <Form.Item name={"searchValue"} noStyle>
                      <Input.Search
                        onSearch={(val) => {
                          setSearchParams((pre) => {
                            pre.set("searchVariant", form1.getFieldValue("searchVariant") ?? "");
                            pre.set("searchValue", val ?? "");
                            return pre;
                          });
                        }}
                        disabled={
                          form1.getFieldValue("searchVariant") === undefined ||
                          form1.getFieldValue("searchVariant") === "searchBy"
                        }
                        allowClear
                        addonBefore={
                          <Form.Item name={"searchVariant"} noStyle>
                            <Select dropdownMatchSelectWidth={false}>
                              <Select.Option disabled={true} value={"searchBy"}>
                                {t("common.searchBy")}
                              </Select.Option>
                              <Select.Option value={"consumerName"}>{t("common.customer")}</Select.Option>
                              <Select.Option value={"phoneNumber"}>{t("common.phoneNumber")}</Select.Option>
                              <Select.Option value={"trackingCode"}>
                                {t("shop.bnpl.ordersList.filters.searchBy.refId")}
                              </Select.Option>
                            </Select>
                          </Form.Item>
                        }
                        placeholder={t("report.search")}
                      />
                    </Form.Item>
                  )}
                </Form.Item>
              </Flex>
              <Flex align={!sm ? "center" : undefined} gap={"middle"} className={sm ? "" : "tw-w-full"}>
                <Button onClick={handleOpenShowFilters} icon={<FiFilter width={12} />} block={!sm}>
                  {t("common.filters")}
                </Button>
                <Button
                  onClick={async () => {
                    const res = await downloadExcel();
                    const { filePath } = res;
                    if (filePath) {
                      window.open(filePath, "_blank");
                    }
                  }}
                  block={!sm}
                  loading={loadingDownloadExcel}
                >
                  {t("common.downloadExcel")}
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Form>
      </Form.Provider>
      {showFilters ? (
        <Drawer
          extra={
            <Flex gap={"middle"} align="center">
              <Button onClick={handleClearFilters}>{t("common.removeFilters")}</Button>
              <Button
                type="primary"
                onClick={() => {
                  drawerForm.submit();
                }}
              >
                {t("common.applyFilters")}
              </Button>
            </Flex>
          }
          open={showFilters}
          onClose={handleCloseShowFilters}
          placement="left"
          size="default"
        >
          <Form
            form={drawerForm}
            layout="vertical"
            initialValues={{
              status: "all",
              creditAmount: parsedFilters.creditAmount,
            }}
            onFinish={(data) => {
              setSearchParams((pre) => {
                pre.set("creditAmount", data.creditAmount ? String(data.creditAmount) : "");
                pre.set("status", data.status ? String(data.status) : "");
                handleCloseShowFilters();
                return pre;
              });
            }}
          >
            <Form.Item label={t("shop.bnpl.creditAmount")} name={"creditAmount"} required>
              <Input.Number
                inputMode="numeric"
                changeOnWheel={false}
                controls={false}
                className="tw-w-full"
                separateThousandth
                step={1000}
                suffix={<CuToman width={24} height={24} />}
              />
            </Form.Item>
            <Form.Item label={t("common.status")} name={"status"}>
              <Select
                options={[
                  { label: t("shop.bnpl.ordersList.filters.status.all"), value: "all" },
                  { label: t("shop.bnpl.ordersList.filters.status.paying"), value: OrderStatusRequest.Paying },

                  { label: t("shop.bnpl.ordersList.filters.status.completed"), value: OrderStatusRequest.Completed },

                  { label: t("shop.bnpl.ordersList.filters.status.overdue"), value: OrderStatusRequest.Delayed },
                  { label: t("shop.bnpl.ordersList.filters.status.defaulted"), value: OrderStatusRequest.Defaulted },
                  {
                    label: t("shop.bnpl.ordersList.filters.status.semiCanceled"),
                    value: OrderStatusRequest.SemiCanceled,
                  },
                  {
                    label: t("shop.bnpl.ordersList.filters.status.salaryDeductionWaiting"),
                    value: OrderStatusRequest.SalaryDeductionWaiting,
                  },
                  {
                    label: t("shop.bnpl.ordersList.filters.status.chequeDeliveryAcceptWaiting"),
                    value: OrderStatusRequest.ChequeDeliveryAcceptWaiting,
                  },
                  { label: t("shop.bnpl.ordersList.filters.status.canceled"), value: OrderStatusRequest.Canceled },
                ]}
              />
            </Form.Item>
          </Form>
        </Drawer>
      ) : null}
    </>
  );
};

export default BnplListPageFilters;
